import React, { useMemo } from 'react';

import {
  Alert,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { SlidingScalePricingItem } from '~/types/tours/costPrice';

import { ShareLabel, hasLinkedShare } from './OfferSplit';
import { ScaleAnnotation, configurationLabelKey, matchesAnnotationSeason } from './departureInventory';
import { formatAmount } from './formatAmount';
import { buildSlidingScaleRows, getPaxPoints } from './slidingScaleRows';

interface Props {
  pricing: Array<SlidingScalePricingItem>;
  /** Marks where one departure sits, saving a repeat of the scale further down. */
  annotation?: ScaleAnnotation;
  /** Fixed group prices have only a per-pax share, with no supplement. */
  showSingleSupplement?: boolean;
}

/**
 * Pinned identity columns, each sized to its own header and values.
 *
 * Total pinned width is deliberately kept modest: it is subtracted from the
 * space left for the pax columns, which are what the table exists to show.
 */
const STICKY_COLUMNS = [
  { key: 'season', width: 85 },
  { key: 'configuration', width: 170 },
  { key: 'singleSupplement', width: 150 },
  { key: 'currency', width: 95 },
] as const;

type StickyColumn = (typeof STICKY_COLUMNS)[number]['key'];

const stickyIndex = (column: StickyColumn) => STICKY_COLUMNS.findIndex(({ key }) => key === column);

const stickyWidth = (column: StickyColumn) => STICKY_COLUMNS[stickyIndex(column)].width;

const stickyLeft = (column: StickyColumn, showSingleSupplement: boolean) =>
  STICKY_COLUMNS.slice(0, stickyIndex(column))
    .filter(({ key }) => showSingleSupplement || key !== 'singleSupplement')
    .reduce((left, { width }) => left + width, 0);

const LAST_STICKY_COLUMN = STICKY_COLUMNS[STICKY_COLUMNS.length - 1].key;

const STICKY_TOTAL_WIDTH = STICKY_COLUMNS.reduce((total, { width }) => total + width, 0);

/** Wide enough for a grouped nine figure amount without wrapping. */
const PAX_COLUMN_WIDTH = 125;

// Currencies like VND run past that; right-aligned, they would overflow under the pinned columns.
const paxColumnWidth = (rows: Array<{ bands: Array<{ unitPrice: number }> }>): number => {
  const longest = Math.max(0, ...rows.flatMap((row) => row.bands.map((band) => formatAmount(band.unitPrice).length)));
  return Math.max(PAX_COLUMN_WIDTH, longest * 9 + 40);
};

/**
 * Every sliding scale row on the service in one table, laid out like the
 * supplier CSV: one row per season and configuration, one column per pax count.
 */
export default function SlidingScaleTable({ pricing, annotation, showSingleSupplement = true }: Props) {
  const shares = annotation?.shares ?? [];
  const isSplit = hasLinkedShare(shares);
  const rows = useMemo(() => buildSlidingScaleRows(pricing), [pricing]);
  const paxPoints = useMemo(() => getPaxPoints(rows), [rows]);
  const paxWidth = useMemo(() => paxColumnWidth(rows), [rows]);

  /**
   * The pax column each row is currently being bought at, by row key.
   *
   * Only rows priced for the departure's own season are marked: a row for
   * another season prices a different departure and says nothing about this one.
   */
  const soldAtByRow = useMemo(() => {
    const marks = new Map<string, number>();

    if (!annotation) {
      return marks;
    }

    rows.forEach((row) => {
      if (!matchesAnnotationSeason(row.season, annotation.season)) {
        return;
      }

      const sold = annotation.soldByConfiguration[configurationLabelKey(row.configurationLabel)] ?? 0;

      if (sold < 1 || !row.bands.length) {
        return;
      }

      // Past the end of the scale the largest quoted price is what applies.
      marks.set(row.key, Math.min(sold, row.bands.length));
    });

    return marks;
  }, [annotation, rows]);

  if (!rows.length || !paxPoints.length) {
    return (
      <Alert severity="info">
        No sliding scale recorded against this service yet. Upload one to populate this table.
      </Alert>
    );
  }

  const stickySx = (column: StickyColumn, zIndex: number) =>
    ({
      position: 'sticky',
      left: stickyLeft(column, showSingleSupplement),
      zIndex,
      // min, width and max together stop content widening the column and
      // knocking the columns pinned after it out of alignment.
      minWidth: stickyWidth(column),
      width: stickyWidth(column),
      maxWidth: stickyWidth(column),
      boxSizing: 'border-box',
      bgcolor: 'background.paper',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      ...(column === LAST_STICKY_COLUMN ? { borderRight: '1px solid', borderRightColor: 'divider' } : {}),
    }) as const;

  return (
    <Stack spacing={1} sx={{ minWidth: 0, maxWidth: '100%' }}>
      <TableContainer sx={{ maxWidth: '100%', overflowX: 'auto' }}>
        {/* table-layout: fixed is what makes the declared column widths real.
            Under the default auto layout the browser sizes columns to content
            and treats width as a hint, which would leave the pinned columns
            offset from where they are actually rendered. */}
        <Table
          size="small"
          stickyHeader
          sx={{
            tableLayout: 'fixed',
            width:
              STICKY_TOTAL_WIDTH -
              (showSingleSupplement ? 0 : stickyWidth('singleSupplement')) +
              paxPoints.length * paxWidth,
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={stickySx('season', 4)}>Season</TableCell>
              <TableCell sx={stickySx('configuration', 4)}>Configuration</TableCell>
              {/* Left aligned so the value sits next to the configuration it
                  belongs to, rather than against the far edge of the column. */}
              {showSingleSupplement && <TableCell sx={stickySx('singleSupplement', 4)}>Single supplement</TableCell>}
              <TableCell sx={stickySx('currency', 4)}>Currency</TableCell>
              {paxPoints.map((pax) => (
                <TableCell key={pax} align="right" sx={{ width: paxWidth, whiteSpace: 'nowrap' }}>
                  {pax} pax
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => {
              const bandsByPax = new Map(row.bands.map((band) => [band.pax, band.unitPrice]));
              const soldAt = soldAtByRow.get(row.key);
              const singles = annotation?.singlesByConfiguration[configurationLabelKey(row.configurationLabel)] ?? 0;
              const sold = annotation?.soldByConfiguration[configurationLabelKey(row.configurationLabel)] ?? 0;
              // The band is this tour's own travellers. The rows beneath list everyone on the departure,
              // since an LTLE built on the tour shares it, though it is priced on its own scale.
              const rowShares =
                isSplit && matchesAnnotationSeason(row.season, annotation?.season)
                  ? shares.filter((share) => !!share.byConfiguration[configurationLabelKey(row.configurationLabel)])
                  : [];

              return (
                <React.Fragment key={row.key}>
                  <TableRow hover>
                    <TableCell sx={stickySx('season', 2)}>{row.season}</TableCell>
                    <TableCell sx={stickySx('configuration', 2)}>
                      <Typography variant="body2" fontWeight="medium" noWrap title={row.configurationLabel}>
                        {row.configurationLabel}
                      </Typography>
                      {soldAt !== undefined && (
                        <Typography variant="caption" color="primary.main" noWrap display="block">
                          {sold} sold
                        </Typography>
                      )}
                    </TableCell>
                    {showSingleSupplement && (
                      <TableCell sx={stickySx('singleSupplement', 2)}>
                        {formatAmount(row.singleSupplement)}
                        {!!annotation && matchesAnnotationSeason(row.season, annotation.season) && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {singles !== 0 ? `${singles} ` : 'No'} single passenger{singles === 1 ? '' : 's'}
                          </Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell sx={stickySx('currency', 2)}>
                      <Chip size="small" label={row.currency} />
                    </TableCell>
                    {paxPoints.map((pax) => {
                      const isSoldAt = soldAt === pax;

                      return (
                        <TableCell
                          key={pax}
                          align="right"
                          sx={{
                            width: paxWidth,
                            whiteSpace: 'nowrap',
                            ...(isSoldAt
                              ? {
                                  bgcolor: 'primary.main',
                                  color: 'primary.contrastText',
                                  fontWeight: 700,
                                }
                              : {}),
                          }}
                        >
                          {bandsByPax.has(pax) ? formatAmount(bandsByPax.get(pax)) : '—'}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                  {rowShares.map((share) => {
                    const key = configurationLabelKey(row.configurationLabel);
                    const units = share.byConfiguration[key] ?? 0;
                    const shareSingles = share.singlesByConfiguration[key] ?? 0;
                    const bookings = share.bookingsByConfiguration[key] ?? 0;
                    const bandPrice = share.isOwn && soldAt !== undefined ? bandsByPax.get(soldAt) : undefined;
                    const cost =
                      bandPrice === undefined ? null : units * bandPrice + shareSingles * row.singleSupplement;

                    return (
                      <TableRow key={share.tourOfferId ?? 'own'}>
                        <TableCell sx={{ ...stickySx('season', 2), py: 0.5 }} />
                        <TableCell sx={{ ...stickySx('configuration', 2), py: 0.5 }}>
                          <ShareLabel share={share} />
                        </TableCell>
                        {showSingleSupplement && <TableCell sx={{ ...stickySx('singleSupplement', 2), py: 0.5 }} />}
                        <TableCell sx={{ ...stickySx('currency', 2), py: 0.5 }} />
                        <TableCell colSpan={paxPoints.length} sx={{ py: 0.5 }}>
                          <Typography variant="caption" color="text.secondary">
                            {units} traveller{units === 1 ? '' : 's'}
                            {shareSingles > 0 && ` (${shareSingles} single)`} · {bookings} booking
                            {bookings === 1 ? '' : 's'}
                            {cost !== null && ` · ${formatAmount(cost)} ${row.currency} at the ${soldAt}-pax rate`}
                            {!share.isOwn && ' · on the shared departure, priced on its own sliding scale'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Box>
        <Typography variant="caption" color="text.secondary">
          Cost is per pax at each booked pax count, in the supplier&apos;s currency.
          {!!annotation && soldAtByRow.size > 0 && <> Highlighted: where {annotation.departureName} sits.</>}
        </Typography>
      </Box>
    </Stack>
  );
}
